// Developer-options escape hatch for reviewing the AI failure screens.
//
// Provoking a real rate limit or an AiAccessDenied takes account-level setup, and NotFoodError needs
// a deliberately useless photo — so the error states were effectively unreviewable. The developer
// options panel writes the chosen failure here and the scan containers swap it in for the real
// response.
//
// The panel itself only renders outside production (DeveloperOptionsContainer returns null there),
// but this helper is compiled into every build, so it also refuses to act on a production bundle.

const STORAGE_KEY = 'developerOptionsForcedAiError';

// Maps the panel's choice onto the GraphQL __typename each screen branches on. 'generic' falls
// through to an unrecognised typename, which is exactly how the containers detect an unexpected
// response.
const FORCED_TYPENAME: Record<string, string> = {
  notFood: 'NotFoodError',
  rateLimit: 'RateLimitExceeded',
  aiAccessDenied: 'AiAccessDenied',
  generic: 'DeveloperOptionsForcedGenericError',
};

/**
 * Returns a stand-in response for the failure selected in developer options, or `null` when nothing
 * is selected (the normal case) — callers keep the real response then.
 */
export const getForcedAiErrorResponse = (): { __typename: string } | null => {
  if (import.meta.env.VITE_REACT_APP_ENV === 'prod') return null;
  try {
    const selected = localStorage.getItem(STORAGE_KEY);
    if (!selected || selected === 'off') return null;
    const typename = FORCED_TYPENAME[selected];
    return typename ? { __typename: typename } : null;
  } catch {
    // Private-mode Safari can throw on localStorage access; never break a real scan over it.
    return null;
  }
};

/**
 * Wraps a real response, substituting the failure selected in developer options when there is one.
 * Keeps the caller's response type so containers don't need a cast:
 *
 *   const response = withForcedAiError(await extractIngredients({ ... }));
 */
export const withForcedAiError = <T>(response: T): T => {
  const forced = getForcedAiErrorResponse();
  return forced ? (forced as T) : response;
};
